import Cookies from 'js-cookie'

const MULTI_ACCOUNTS_KEY = 'welink_multi_accounts'
const CURRENT_ACCOUNT_KEY = 'welink_current_account_id'

class MultiAccountManager {
  constructor() {
    this.accounts = this.loadAccounts()
  }

  loadAccounts() {
    try {
      const stored = localStorage.getItem(MULTI_ACCOUNTS_KEY)
      return stored ? JSON.parse(stored) : {}
    } catch (error) {
      console.error('Failed to load accounts from localStorage:', error)
      return {}
    }
  }

  saveAccounts() {
    try {
      localStorage.setItem(MULTI_ACCOUNTS_KEY, JSON.stringify(this.accounts))
    } catch (error) {
      console.error('Failed to save accounts to localStorage:', error)
    }
  }

  addAccount(accountData) {
    const accountId = accountData.user_id || accountData._id?.$oid || accountData._id?.$id
    if (!accountId) {
      console.error('Invalid account data: missing user_id')
      return false
    }

    const accountInfo = {
      id: accountId,
      user_id: accountId,
      user_name: accountData.user_name || accountData.user_nickName || '',
      user_email: accountData.user_email || accountData.student_email || '',
      user_avatar_address_small: accountData.user_avatar_address_small || '',
      user_type: accountData.user_type || 'individual',
      account_type: accountData.account_type || '',
      account_role: accountData.account_role || 'subscriber',
      creator_functionality_level: accountData.creator_functionality_level || 'Basic',
      login_time: new Date().toISOString(),
      last_active: new Date().toISOString()
    }

    this.accounts[accountId] = accountInfo
    this.saveAccounts()
    this.setCurrentAccount(accountId)
    
    return true
  }

  updateAccount(accountId, updates) {
    if (this.accounts[accountId]) {
      this.accounts[accountId] = {
        ...this.accounts[accountId],
        ...updates,
        last_active: new Date().toISOString()
      }
      this.saveAccounts()
      return true
    }
    return false
  }

  removeAccount(accountId) {
    if (this.accounts[accountId]) {
      delete this.accounts[accountId]
      this.saveAccounts()
      
      const currentAccountId = this.getCurrentAccountId()
      if (currentAccountId === accountId) {
        const remainingAccountIds = Object.keys(this.accounts)
        if (remainingAccountIds.length > 0) {
          this.switchToAccount(remainingAccountIds[0])
        } else {
          localStorage.removeItem(CURRENT_ACCOUNT_KEY)
          this.clearAllCookies()
        }
      }
      return true
    }
    return false
  }

  getAccount(accountId) {
    return this.accounts[accountId] || null
  }

  getAllAccounts() {
    return Object.values(this.accounts).sort((a, b) => {
      return new Date(b.last_active) - new Date(a.last_active)
    })
  }

  getCurrentAccountId() {
    return localStorage.getItem(CURRENT_ACCOUNT_KEY) || Cookies.get('user_id') || null
  }

  setCurrentAccount(accountId) {
    localStorage.setItem(CURRENT_ACCOUNT_KEY, accountId)
    
    if (this.accounts[accountId]) {
      this.updateAccount(accountId, { last_active: new Date().toISOString() })
    }
  }

  switchToAccount(accountId) {
    const account = this.getAccount(accountId)
    if (!account) {
      console.error('Account not found:', accountId)
      return false
    }

    this.clearAllCookies()

    Cookies.set('user_id', account.user_id)
    Cookies.set('user_name', account.user_name)
    Cookies.set('user_avatar_address_small', account.user_avatar_address_small)
    Cookies.set('user_type', account.user_type)
    Cookies.set('account_type', account.account_type || '')
    Cookies.set('account_role', account.account_role || 'subscriber')
    Cookies.set('creator_functionality_level', account.creator_functionality_level)
    
    this.setCurrentAccount(accountId)
    
    return true
  }

  extractCookies() {
    const allCookies = {}
    const cookieKeys = [
      'user_id',
      'user_name', 
      'user_avatar_address_small',
      'user_type',
      'account_type',
      'account_role',
      'creator_functionality_level',
      'token'
    ]
    
    cookieKeys.forEach(key => {
      const value = Cookies.get(key)
      if (value) {
        allCookies[key] = value
      }
    })
    
    return allCookies
  }

  clearAllCookies() {
    const cookieKeys = [
      'user_id',
      'user_name',
      'user_avatar_address_small', 
      'user_type',
      'account_type',
      'account_role',
      'creator_functionality_level',
      'token'
    ]
    
    cookieKeys.forEach(key => {
      Cookies.remove(key)
    })
  }

  isAccountLoggedIn(accountId) {
    return !!this.accounts[accountId]
  }

  getOtherAccounts(excludeAccountId) {
    return Object.values(this.accounts).filter(account => account.id !== excludeAccountId)
  }

  clearAllAccounts() {
    this.accounts = {}
    this.saveAccounts()
    localStorage.removeItem(CURRENT_ACCOUNT_KEY)
    this.clearAllCookies()
  }
}

export default new MultiAccountManager()
